// src/components/compliance/modals/DraftRecommendationHandoffsModal.jsx
// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

import React, { useCallback, useEffect, useState } from "react";
import ModalShell from "./shared/ModalShell";
import ModalSection from "./shared/ModalSection";
import ModalActionRow from "./shared/ModalActionRow";

const CONNECTOR_PROVIDER_DISPLAY_NAMES = {
  aws: "AWS",
  cloudflare: "Cloudflare",
  github: "GitHub",
  google_workspace: "Google Workspace",
  okta: "Okta",
};

function cleanConnectorProvider(value) {
  return String(value || "").trim().toLowerCase();
}

function formatConnectorProvider(value) {
  const normalizedProvider = cleanConnectorProvider(value);

  if (CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider]) {
    return CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider];
  }

  return String(value || "Unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function titleCaseLabel(value, fallback = "—") {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function formatPriority(value) {
  const normalized = String(value || "medium").trim().toLowerCase();
  if (normalized === "critical") return "Critical";
  if (normalized === "high") return "High";
  if (normalized === "low") return "Low";
  return "Medium";
}

function priorityColor(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "critical" || normalized === "high") return "#fb7185";
  if (normalized === "medium") return "#fbbf24";
  return "#34d399";
}

function normalizeList(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.filter((item) => item !== undefined && item !== null && item !== "");
  }
  return [value].filter((item) => item !== undefined && item !== null && item !== "");
}

function formatList(value, fallback = "—") {
  const items = normalizeList(value)
    .map((item) => String(item).trim())
    .filter(Boolean);

  return items.length ? items.join(", ") : fallback;
}

function formatRefreshError(error) {
  const text = String(error || "").trim();
  if (!text) return "";

  if (text.toLowerCase() === "failed to fetch") {
    return "Draft handoffs could not be refreshed. Check the API connection and try again.";
  }

  return text;
}

const summaryBarStyle = {
  border: "1px solid rgba(96, 165, 250, 0.24)",
  borderRadius: 16,
  padding: "12px 14px",
  background: "rgba(30, 64, 175, 0.12)",
  color: "#bfdbfe",
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "center",
  flexWrap: "wrap",
};

const summaryCountStyle = {
  color: "#f8fafc",
  fontSize: 14,
  fontWeight: 900,
};

const summaryHintStyle = {
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.45,
};

const listStyle = {
  display: "grid",
  gap: 12,
};

const itemCardStyle = {
  border: "1px solid rgba(96, 165, 250, 0.24)",
  borderRadius: 16,
  padding: 16,
  background: "rgba(15, 23, 42, 0.42)",
  display: "grid",
  gap: 12,
};

const chipRowStyle = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
};

const chipStyle = {
  borderRadius: 999,
  padding: "4px 9px",
  fontSize: 11,
  fontWeight: 900,
  background: "rgba(96, 165, 250, 0.14)",
  border: "1px solid rgba(96, 165, 250, 0.22)",
  color: "#bfdbfe",
};

const detailGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 10,
  color: "#bfdbfe",
  fontSize: 13,
};

const bodyTextStyle = {
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.55,
  margin: 0,
};

const noticeStyle = {
  border: "1px solid rgba(96, 165, 250, 0.35)",
  borderRadius: 14,
  padding: "10px 12px",
  background: "rgba(37, 99, 235, 0.12)",
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.45,
};

const errorStyle = {
  ...noticeStyle,
  border: "1px solid rgba(248, 113, 113, 0.38)",
  background: "rgba(127, 29, 29, 0.20)",
  color: "#fecaca",
};

export default function DraftRecommendationHandoffsModal({
  open,
  items = [],
  count = 0,
  loading = false,
  error = "",
  message = "",
  cancellingId = null,
  previewingConvertId = null,
  onClose,
  onRefresh,
  onCancel,
  onPreviewConversion,
}) {
  const draftItems = Array.isArray(items) ? items : [];
  const draftCount = Number.isFinite(Number(count)) ? Number(count) : draftItems.length;
  const formattedError = formatRefreshError(error);
  const [refreshPulse, setRefreshPulse] = useState(false);
  const isRefreshing = Boolean(loading || refreshPulse);

  useEffect(() => {
    if (!open) {
      setRefreshPulse(false);
    }
  }, [open]);

  const handleRefresh = useCallback(() => {
    if (isRefreshing) return;

    setRefreshPulse(true);
    onRefresh?.();

    window.setTimeout(() => {
      setRefreshPulse(false);
    }, 1200);
  }, [isRefreshing, onRefresh]);

  return (
    <ModalShell
      open={open}
      ariaLabel="Draft recommendation handoffs"
      eyebrow="OASYS · Draft Handoffs"
      title="Draft Recommendation Handoffs"
      subtitle="Review planning records created from accepted recommendations. Drafts do not execute remediation or advance workflow."
      onClose={onClose}
      maxWidth="940px"
      accentBorder="rgba(96, 165, 250, 0.52)"
      zIndex={170}
    >
      <div style={summaryBarStyle}>
        <div>
          <div style={summaryCountStyle}>
            {draftCount} draft handoff{draftCount === 1 ? "" : "s"} awaiting operator review
          </div>
          <div style={summaryHintStyle}>
            {isRefreshing
              ? "Refreshing draft handoffs…"
              : "Drafts are planning records only. Cancelling a draft does not change recommendation status."}
          </div>
        </div>

        <button
          type="button"
          className="btn btn-ghost btn-xs"
          onClick={handleRefresh}
          disabled={isRefreshing}
          style={{ whiteSpace: "nowrap" }}
        >
          {isRefreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {message ? <div style={noticeStyle}>{message}</div> : null}
      {formattedError && !isRefreshing ? <div style={errorStyle}>{formattedError}</div> : null}

      <ModalSection
        title="Draft handoff queue"
        description="Saved draft handoffs are planning records only. No remediation task, workflow advancement, evidence approval, or validation run has happened."
        badge={isRefreshing ? "Refreshing" : `${draftItems.length} draft${draftItems.length === 1 ? "" : "s"}`}
        badgeTone={isRefreshing ? "medium" : "default"}
      >
        {isRefreshing ? (
          <p style={bodyTextStyle}>Refreshing draft handoffs…</p>
        ) : draftItems.length === 0 ? (
          <p style={bodyTextStyle}>No draft handoffs are currently awaiting review.</p>
        ) : (
          <div style={listStyle}>
            {draftItems.map((item) => {
              const isCancelling = String(cancellingId || "") === String(item.id || "");
              const isPreviewingConversion = String(previewingConvertId || "") === String(item.id || "");
              const priority = formatPriority(item.priority);
              const connector = item.connector ? formatConnectorProvider(item.connector) : "No connector";

              return (
                <article key={item.id || item.recommendation_key || item.title} style={itemCardStyle}>
                  <div style={chipRowStyle}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <span style={chipStyle}>Draft</span>
                      <span
                        style={{
                          ...chipStyle,
                          color: priorityColor(item.priority),
                          borderColor: priorityColor(item.priority),
                          background: "rgba(15, 23, 42, 0.36)",
                        }}
                      >
                        {priority}
                      </span>
                      <span style={chipStyle}>{titleCaseLabel(item.category || item.handoff_type)}</span>
                      <span style={chipStyle}>{connector}</span>
                    </div>
                    <strong style={{ color: "#bfdbfe", fontSize: 12 }}>
                      Created {formatDateTime(item.created_at)}
                    </strong>
                  </div>

                  <div style={{ display: "grid", gap: 6 }}>
                    <strong style={{ color: "#f8fafc", fontSize: 15, lineHeight: 1.35 }}>
                      {item.title || item.suggested_workflow_title || "Draft recommendation handoff"}
                    </strong>
                    <p style={bodyTextStyle}>
                      {item.summary || "Draft handoff saved for operator review. No work has been executed."}
                    </p>
                  </div>

                  <div style={detailGridStyle}>
                    <div>
                      <strong>Framework:</strong> {item.framework || "—"}
                    </div>
                    <div>
                      <strong>Controls:</strong> {formatList(item.controls)}
                    </div>
                    <div>
                      <strong>Findings:</strong> {formatList(item.findings)}
                    </div>
                    <div>
                      <strong>Resources:</strong> {formatList(item.resources)}
                    </div>
                    <div>
                      <strong>Suggested task:</strong> {item.suggested_task_title || "—"}
                    </div>
                    <div>
                      <strong>Updated:</strong> {formatDateTime(item.updated_at)}
                    </div>
                  </div>

                  <div style={noticeStyle}>
                    Draft only. OASYS has not executed remediation, created a remediation task, approved evidence, advanced a workflow, or run validation.
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="btn btn-primary btn-xs"
                      onClick={() => onPreviewConversion?.(item)}
                      disabled={isPreviewingConversion || isCancelling}
                    >
                      {isPreviewingConversion ? "Preparing preview…" : "Preview conversion"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs"
                      onClick={() => onCancel?.(item)}
                      disabled={isCancelling || isPreviewingConversion}
                    >
                      {isCancelling ? "Cancelling…" : "Cancel draft"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </ModalSection>

      <ModalActionRow
        actions={[
          {
            key: "refresh",
            label: isRefreshing ? "Refreshing…" : "Refresh",
            className: "btn btn-ghost btn-sm",
            disabled: isRefreshing,
            onClick: handleRefresh,
          },
          {
            key: "close",
            label: "Close",
            className: "btn btn-primary btn-sm",
            onClick: onClose,
          },
        ]}
      />
    </ModalShell>
  );
}
